import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import WebContactForm from '../components/WebContactForm';
import { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';
import { SITE_INFO, BRAND_ASSETS } from '../data';

export default function BetonarbejdePage() {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const concreteImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-fundament-opbygning.webp",
      alt: "Betonfundament under opbygning på byggegrund",
      caption: "Betonfundament under opbygning på byggegrund"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-forskalling-armering.webp",
      alt: "Forskalling og armeringsarbejde til beton",
      caption: "Forskalling og armeringsarbejde til beton"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-faerdigt-arbejde.webp",
      alt: "Færdigstøbt betonfundament på byggegrund",
      caption: "Færdigstøbt betonfundament på byggegrund"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-smk-armeringsarbejde.webp",
      alt: "Stort armeringsarbejde ved Statens Museum for Kunst",
      caption: "Stort armeringsarbejde ved Statens Museum for Kunst"
    }
  ];

  const { pathname } = useLocation();
  const page = pathname.replace(/^\//, '') || 'betonarbejde';

  const services = [
    {
      title: 'Fundamenter',
      desc: 'Fundamenter til boliger, tilbygninger, haller og erhvervsbyggeri.',
      icon: 'fa-cubes-stacked'
    },
    {
      title: 'Terrændæk og betongulve',
      desc: 'Klargøring, isolering, armering og støbning af terrændæk og gulve.',
      icon: 'fa-layer-group'
    },
    {
      title: 'Støbte vægge og konstruktioner',
      desc: 'Støbning af vægge, støttemure, elevatorskakte og andre betonkonstruktioner.',
      icon: 'fa-building'
    },
    {
      title: 'Forskalling og armering',
      desc: 'Forskalling samt klipning, bukning og binding af armering.',
      icon: 'fa-shield'
    },
    {
      title: 'Kældre',
      desc: 'Nye kældre fra udgravning til færdigt dæk samt reparation af eksisterende kælderkonstruktioner.',
      icon: 'fa-trowel-bricks'
    }
  ];

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Betonarbejde | Fundamenter, dæk og støbning | KTS-Entreprise A/S"
        description="KTS-Entreprise udfører betonarbejde til nybyggeri, tilbygninger, erhvervsbyggeri og renoveringsopgaver – fra udgravning og fundament til færdigt terrændæk."
        ogTitle="Betonarbejde | KTS-Entreprise A/S"
        ogDescription="Professionelt betonarbejde, fundamenter, støbte vægge og kældre i Nordsjælland og Storkøbenhavn."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="betonarbejde"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Fagområde</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Betonarbejde
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            KTS-Entreprise udfører betonarbejde til nybyggeri, tilbygninger, erhvervsbyggeri og renoveringsopgaver – fra udgravning og fundament til færdigt terrændæk.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Services & Featured SMK Project */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          
          <div className="lg:col-span-7 space-y-6">
            <div className="border-l-4 border-[#0072c0] pl-4">
              <h2 className="text-2xl sm:text-3xl font-extrabold uppercase text-stone-900 font-heading">
                Ydelser inden for betonarbejde
              </h2>
            </div>

            {/* Service Cards */}
            <div className="space-y-3.5">
              {services.map((item) => (
                <div
                  key={item.title}
                  className="p-5 bg-white border border-stone-200 rounded-xs shadow-xs"
                >
                  <div className="flex items-center gap-3 text-[#0072c0] mb-1">
                    <i className={`fa-solid ${item.icon} text-base`}></i>
                    <h3 className="font-heading font-bold uppercase text-stone-900 text-base">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-stone-900 text-white p-6 rounded-xs border border-stone-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-heading font-bold text-base uppercase text-white">
                  Få et uforpligtende tilbud på betonarbejde
                </p>
                <p className="text-stone-400 text-xs mt-1">
                  Vi gennemgår gerne projektet og udfører arbejdet efter det aftalte projektmateriale og gældende tegninger.
                </p>
              </div>
              <a
                href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
                className="bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-6 py-3 text-xs transition-all whitespace-nowrap font-mono rounded-xs"
              >
                <i className="fa-solid fa-phone mr-2"></i>
                {SITE_INFO.mainPhone}
              </a>
            </div>
          </div>

          {/* Right Column: Stages of Concrete Work & Highlighted SMK Project & Sugespids link */}
          <div className="lg:col-span-5 space-y-4">
            
            {/* 1. Opbygning / Fundament */}
            <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs">
              <ClickableWorkImage
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${concreteImages[0].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                alt={concreteImages[0].alt}
                className="w-full h-auto max-h-[320px] object-cover rounded-xs"
                onClick={() => setLightboxState({ isOpen: true, index: 0 })}
              />
            </div>

            {/* Internal Link: Sugespidsanlæg */}
            <div className="bg-slate-50 border border-slate-200 p-5 rounded-xs space-y-2 text-xs">
              <span className="font-heading font-bold uppercase text-stone-900 block">
                Betonarbejde under grundvandsniveau?
              </span>
              <p className="text-stone-600">
                Vi råder over eget sugespidsanlæg til grundvandssænkning ved kældre, fundamenter og udgravninger.
              </p>
              <Link
                to="/entreprenerservice#sugespidsanlaeg"
                className="inline-flex items-center gap-1.5 font-heading font-bold uppercase text-[#0072c0] hover:text-[#005b99] pt-1"
              >
                <span>Læs om sugespidsanlæg</span>
                <i className="fa-solid fa-arrow-right text-[10px]"></i>
              </Link>
            </div>

          </div>

        </div>

        {/* Billeder fra vores betonarbejde & fremhævet projekt */}
        <section className="mb-16">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase text-stone-900 font-heading">
              Billeder fra vores betonarbejde
            </h2>
            <p className="text-stone-600 text-xs sm:text-sm mt-1">
              Eksempler på udførte fundamenter, armeringsarbejde og særlige betonkonstruktioner.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Forskalling & armering */}
            <div className="bg-white border border-stone-200 p-3 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${concreteImages[1].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={concreteImages[1].alt}
                  className="w-full h-52 sm:h-56 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 1 })}
                />
              </div>
              <div className="pt-3 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Armering
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Forskalling og armeringsarbejde til beton
                </p>
              </div>
            </div>

            {/* Færdigt arbejde */}
            <div className="bg-white border border-stone-200 p-3 rounded-xs shadow-xs flex flex-col justify-between">
              <div className="overflow-hidden rounded-xs bg-stone-100">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${concreteImages[2].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={concreteImages[2].alt}
                  className="w-full h-52 sm:h-56 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 2 })}
                />
              </div>
              <div className="pt-3 px-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Fundament
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase mt-0.5">
                  Færdigstøbt betonfundament på byggegrund
                </p>
              </div>
            </div>

            {/* Fremhævet projekt: Statens Museum for Kunst */}
            <div className="bg-white border border-stone-200 rounded-xs shadow-xs overflow-hidden flex flex-col justify-between">
              <div className="relative aspect-16/10 bg-stone-200">
                <ClickableWorkImage
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${concreteImages[3].src.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                  alt={concreteImages[3].alt}
                  className="w-full h-full object-cover"
                  onClick={() => setLightboxState({ isOpen: true, index: 3 })}
                />
                <span className="absolute top-3 left-3 bg-[#0072c0] text-white text-[11px] font-heading font-bold uppercase tracking-wider px-2.5 py-1 rounded-xs pointer-events-none">
                  Fremhævet projekt
                </span>
              </div>
              
              <div className="p-5 flex-grow flex flex-col justify-between">
                <div>
                  <h3 className="font-heading font-bold text-base text-stone-900 uppercase mb-1.5">
                    Spejlbassin ved Statens Museum for Kunst
                  </h3>
                  <p className="text-stone-600 text-xs leading-relaxed mb-4">
                    Cirkulært spejlbassin på 700 m² med 80 tons armeringsjern og specialfremstillede Gote-kantelementer foran SMK.
                  </p>
                </div>
                <Link
                  to="/smk"
                  className="inline-flex items-center justify-center w-full gap-2 bg-stone-900 hover:bg-[#0072c0] text-white font-heading font-bold uppercase tracking-wider py-2.5 px-4 text-xs transition-colors rounded-xs"
                >
                  <span>Læs hele projektet</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Formular */}
        <div className="mt-16">
          <WebContactForm
            title="Få et tilbud på betonarbejde"
            subtitle="Udfyld formularen med dine projektoplysninger, så vender vi tilbage med en vurdering."
            submitText="Send forespørgsel"
            showAddress={true}
            showTaskTypeSelect={true}
            taskTypeOptions={[
              "Fundamenter til bolig / tilbygning",
              "Fundamenter til hal / erhvervsbyggeri",
              "Terrændæk og betongulve",
              "Støbte vægge / støttemure",
              "Forskalling og armering",
              "Kælderkonstruktion eller renovering",
              "Andet betonarbejde"
            ]}
            showFileUpload={true}
            fileLabel="Vedhæft gerne ingeniørtegninger eller projektmateriale (valgfrit)"
          />
        </div>

        {/* Lightbox for Betonarbejde */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={concreteImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="Betonarbejde"
        />

      </div>
    </div>
  );
}
